import {
  createDocFromUint8Array,
  type Template,
  TemplateType,
  type TemplateVersion,
} from '@piccolohealth/pbs-common';
import { ChakraV3, dataAttr, FancyDate, ScrollArea } from '@piccolohealth/ui';
import { inflection, P } from '@piccolohealth/util';
import { generateHTML } from '@tiptap/html';
import React from 'react';
import scrollIntoView from 'scroll-into-view-if-needed';
import { createModal } from '../../components/generic/Modal';
import { Editor } from '../../components/tiptap/Editor';
import { EditorOutlineSection } from '../../components/tiptap/EditorOutlineSection';
import { useEditor } from '../../components/tiptap/hooks/useEditor';
import type { HocuspocusState } from '../../components/tiptap/hooks/useHocuspocusProvider';
import { EditorLayout } from '../../components/tiptap/layout/EditorLayout';
import {
  BASE_EXTENSIONS,
  convertYDocToJSONContent,
  getParticipantDocumentTemplateExtensions,
  getParticipantNoteTemplateExtensions,
  postProcessContent,
} from '../../components/tiptap/utils';
import { UserAvatar } from '../../components/user/UserAvatar';
import { useRestoreTemplateVersionMutation } from '../../graphql/hooks/useRestoreTemplateVersionMutation';
import {
  useTemplateVersionQuery,
  useTemplateVersionsInfiniteQuery,
} from '../../graphql/hooks/useTemplateQuery';
import { useAppContext } from '../../hooks/useAppContext';

interface ParticipantDocumentHistorySidebarProps {
  templateId: string;
  activeVersionId: string | null;
  setActiveVersionId: (versionId: string) => void;
}

const TemplateHistorySidebar = (props: ParticipantDocumentHistorySidebarProps) => {
  const { templateId, activeVersionId, setActiveVersionId } = props;
  const { organization } = useAppContext();

  const endRef = React.useRef<HTMLDivElement>(null);

  const templateVersionsQuery = useTemplateVersionsInfiniteQuery({
    organizationId: organization.id,
    templateId,
    request: {
      pagination: {
        limit: 40,
      },
    },
  });

  const templateVersionsPagination = P.first(templateVersionsQuery.data?.pages ?? [])?.organization
    ?.template?.versions?.pagination;

  const templateVersions =
    templateVersionsQuery.data?.pages.flatMap(
      (page) => page.organization?.template?.versions.results as TemplateVersion[],
    ) ?? [];

  const latestVersion = P.first(templateVersions);
  const isLoading = templateVersionsQuery.isLoading;
  const hasNextPage = templateVersionsQuery.hasNextPage;
  const total = templateVersionsPagination?.total ?? 0;

  const fetchNextPage = () => {
    templateVersionsQuery.fetchNextPage();
  };

  // https://github.com/biomejs/biome/issues/630
  // biome-ignore lint/correctness/useExhaustiveDependencies: We want to run this effect when the number of pages changes
  React.useEffect(() => {
    if (endRef.current) {
      scrollIntoView(endRef.current, {
        scrollMode: 'if-needed',
        block: 'end',
        inline: 'nearest',
        behavior: 'smooth',
      });
    }
  }, [templateVersionsQuery.data?.pages.length]);

  React.useEffect(() => {
    // TODO: Remove the versionId nil check when we make it required.
    if (!P.isNil(latestVersion) && !P.isNil(latestVersion.versionId)) {
      setActiveVersionId(latestVersion.versionId);
    }
  }, [latestVersion, setActiveVersionId]);

  if (!templateVersionsPagination) {
    return null;
  }

  const content = P.run(() => {
    if (P.isEmpty(templateVersions)) {
      return (
        <ChakraV3.Text fontSize='xs' fontWeight='bold' color='secondary'>
          No versions found
        </ChakraV3.Text>
      );
    }

    return (
      <>
        <ChakraV3.Text fontSize='xs' fontWeight='bold' color='secondary'>
          Versions
        </ChakraV3.Text>
        <ScrollArea flexDir='column' overflowY='auto' h='full'>
          <ChakraV3.Stack pr={4}>
            {templateVersions.map((document, index) => (
              <ChakraV3.Button
                as={ChakraV3.HStack}
                key={document.versionId}
                {...{ 'data-active': dataAttr(activeVersionId === document.versionId) }}
                onClick={() => {
                  // TODO: Remove the versionId nil check when we make it required.
                  if (!P.isNil(document.versionId)) {
                    setActiveVersionId(document.versionId);
                  }
                }}
                variant='ghost'
                color='gray.600'
                size='sm'
                justifyContent='start'
                whiteSpace='initial'
                h='auto'
                px={3}
                py={2}
                rounded='md'
                fontWeight='semibold'
              >
                <ChakraV3.HStack w='full' align='start'>
                  <ChakraV3.Stack gap={1}>
                    <ChakraV3.Text fontWeight='semibold'>Version {total - index}</ChakraV3.Text>
                    <FancyDate
                      fontSize='xs'
                      color='secondary'
                      date={document.createdAt}
                      showTooltip={false}
                      direction='row'
                    />
                  </ChakraV3.Stack>
                  <ChakraV3.Spacer />
                  <ChakraV3.AvatarGroup gap={-3}>
                    {P.take(document.users, 3).map((member) => (
                      <UserAvatar
                        key={member.id}
                        name={member.name}
                        secondary={member.email}
                        picture={member.picture}
                        showTooltip
                        size='sm'
                      />
                    ))}
                  </ChakraV3.AvatarGroup>
                </ChakraV3.HStack>
              </ChakraV3.Button>
            ))}
            <ChakraV3.Stack ref={endRef} />
          </ChakraV3.Stack>
        </ScrollArea>
        <ChakraV3.Spacer />
        <ChakraV3.Separator />
        <ChakraV3.HStack pr={4}>
          <ChakraV3.Text fontSize='xs' whiteSpace='nowrap'>
            {templateVersionsPagination.total}{' '}
            {inflection.inflect('version', templateVersionsPagination.total)}
          </ChakraV3.Text>
          <ChakraV3.Spacer minW={4} />
          <ChakraV3.Button
            onClick={fetchNextPage}
            variant='plain'
            fontSize='xs'
            disabled={!hasNextPage}
            loading={isLoading}
            justifyContent='end'
          >
            Load more
          </ChakraV3.Button>
        </ChakraV3.HStack>
      </>
    );
  });

  return (
    <ChakraV3.Stack
      h='full'
      w='320px'
      gap={4}
      flexShrink={0}
      borderRightWidth='1px'
      borderRightColor='gray.200'
    >
      {content}
    </ChakraV3.Stack>
  );
};

interface Props {
  template: Template;
}

export const TemplateVersionHistoryModal = createModal<Props>((props) => {
  const { modal, template } = props;
  const { remove, hide, visible } = modal;
  const { organization, successToast, errorToast, location } = useAppContext();

  const [activeVersionId, setActiveVersionId] = React.useState<string | null>(null);

  const restoreTemplateVersionMutation = useRestoreTemplateVersionMutation();

  const activeTemplateQuery = useTemplateVersionQuery(
    {
      organizationId: organization.id,
      templateId: template.id,
      versionId: activeVersionId ?? '',
    },
    {
      enabled: activeVersionId !== null,
    },
  );

  const activeTemplate = activeTemplateQuery.data?.organization?.template;

  const state: HocuspocusState = P.run(() => {
    if (activeTemplateQuery.isFetching) {
      return { status: 'connecting' };
    }

    if (activeTemplateQuery.isError) {
      return { status: 'disconnected' };
    }

    return { status: 'connected', save: { saveStatus: 'saved', saveCount: 0, lastSavedAt: null } };
  });

  const extensions = React.useMemo(() => {
    if (!activeTemplate) {
      return BASE_EXTENSIONS;
    }

    switch (activeTemplate.type) {
      case TemplateType.ParticipantDocument: {
        return getParticipantDocumentTemplateExtensions({
          variables: [],
        });
      }
      case TemplateType.ParticipantNote:
        return getParticipantNoteTemplateExtensions({});
      default:
        return BASE_EXTENSIONS;
    }
  }, [activeTemplate]);

  const content = React.useMemo(() => {
    const value = activeTemplate?.value ?? undefined;

    if (!value) {
      return undefined;
    }

    const document = createDocFromUint8Array(value);
    const jsonContent = convertYDocToJSONContent(document);
    const processed = postProcessContent(jsonContent, []);
    const htmlContent = generateHTML(processed, extensions);

    return htmlContent;
  }, [activeTemplate?.value, extensions]);

  const { editor } = useEditor(
    {
      extensions,
      isEditable: false,
      content,
    },
    [extensions, content],
  );

  const onRestoreVersion = React.useCallback(async () => {
    await restoreTemplateVersionMutation
      .mutateAsync({
        organizationId: organization.id,
        templateId: template.id,
        versionId: activeVersionId ?? '',
      })
      .then(() => {
        hide();
        successToast('Version restored successfully');
        location.reload();
      })
      .catch((err) => {
        errorToast(`Error restoring version: ${err.message}`);
      });
  }, [
    activeVersionId,
    location,
    organization.id,
    restoreTemplateVersionMutation,
    template.id,
    hide,
    successToast,
    errorToast,
  ]);

  return (
    <ChakraV3.Dialog.Root
      open={visible}
      onOpenChange={hide}
      onExitComplete={remove}
      size='cover'
      scrollBehavior='inside'
    >
      <ChakraV3.Dialog.Backdrop />
      <ChakraV3.Dialog.Positioner>
        <ChakraV3.Dialog.Content>
          <ChakraV3.DialogTitleSubtitle
            title={`${template.name} - History`}
            subtitle='You can view the history of this template here.'
          />
          <ChakraV3.Dialog.Body>
            <ChakraV3.HStack w='full' h='full' gap={4}>
              <TemplateHistorySidebar
                templateId={template.id}
                activeVersionId={activeVersionId}
                setActiveVersionId={setActiveVersionId}
              />
              <ChakraV3.Stack w='full' h='full' align='center'>
                <ChakraV3.Stack h='full' w='full' overflowY='auto' layerStyle='fade'>
                  {activeTemplate && (
                    <EditorLayout
                      documentId={activeTemplate.id}
                      isSidebarOpen={true}
                      state={state}
                      headerContent={
                        <ChakraV3.Heading size='3xl' fontWeight='bold' lineClamp={1}>
                          {activeTemplate.name}
                        </ChakraV3.Heading>
                      }
                      toolbarContent={null}
                      mainContent={<Editor editor={editor} />}
                      sidebarContent={<EditorOutlineSection editor={editor} />}
                    />
                  )}
                </ChakraV3.Stack>
                <ChakraV3.AlertBanner
                  status='warning'
                  alignItems='center'
                  fontSize='md'
                  title='Restoring this version will overwrite the current document. Are you sure you
                    want to restore this version?'
                  endElement={
                    <ChakraV3.HStack>
                      <ChakraV3.Button size='sm' onClick={hide} variant='subtle'>
                        Close
                      </ChakraV3.Button>
                      <ChakraV3.Button
                        colorPalette='brand'
                        size='sm'
                        onClick={onRestoreVersion}
                        loading={restoreTemplateVersionMutation.isLoading}
                      >
                        Restore version
                      </ChakraV3.Button>
                    </ChakraV3.HStack>
                  }
                />
              </ChakraV3.Stack>
            </ChakraV3.HStack>
          </ChakraV3.Dialog.Body>
        </ChakraV3.Dialog.Content>
      </ChakraV3.Dialog.Positioner>
    </ChakraV3.Dialog.Root>
  );
});
